// Kept identical to apps/belong-next/src/features/action-items/invitation-sent.utils.ts apart from the strings import.
import { template } from '@belong/common';
import { UPDATED_AGREEMENT_TO_DO_STRINGS as STRINGS } from '../strings/to-do.strings';
import type {
  HomeInAgreement,
  InvitationSentToDoMetadata,
  ReplacementGroupMetadata,
} from './types';

type InvitationVariant = 'first' | 'update' | 'split';

type GroupedToDo<T> = {
  todo: T;
  homes: HomeInAgreement[];
  group?: ReplacementGroupMetadata;
};

export function getInvitationVariant({
  replacesAgreementId,
  replacementGroup,
}: Pick<
  InvitationSentToDoMetadata,
  'replacesAgreementId' | 'replacementGroup'
>): InvitationVariant {
  if (replacementGroup) {
    return 'split';
  }

  return replacesAgreementId != null ? 'update' : 'first';
}

export function getInvitationSentToDoTitle({
  metadata,
  invitationTitle,
}: {
  metadata: Pick<
    InvitationSentToDoMetadata,
    'replacesAgreementId' | 'replacementGroup'
  >;
  invitationTitle: string;
}): string {
  const titles: Record<InvitationVariant, string> = {
    first: invitationTitle,
    update: STRINGS.updatedAgreement,
    split: STRINGS.splitAgreements,
  };

  return titles[getInvitationVariant(metadata)];
}

export function signedProgress({
  viewerSignedCount,
  viewerSignerCount,
}: Pick<
  InvitationSentToDoMetadata,
  'viewerSignedCount' | 'viewerSignerCount'
>): string | undefined {
  if (
    viewerSignedCount == null ||
    viewerSignerCount == null ||
    viewerSignerCount <= 1
  ) {
    return undefined;
  }

  return template(STRINGS.signedProgress, {
    signed: viewerSignedCount,
    total: viewerSignerCount,
  });
}

export function awaitingCosignersCopy({
  pendingCosignerCount,
  viewerSignedCount,
  viewerSignerCount,
}: Pick<
  InvitationSentToDoMetadata,
  'pendingCosignerCount' | 'viewerSignedCount' | 'viewerSignerCount'
>): string | undefined {
  if (
    pendingCosignerCount == null ||
    pendingCosignerCount <= 0 ||
    viewerSignedCount == null ||
    viewerSignerCount == null ||
    viewerSignedCount >= viewerSignerCount
  ) {
    return undefined;
  }

  if (pendingCosignerCount === 1) {
    return STRINGS.awaitingCosigner;
  }

  return template(STRINGS.awaitingCosigners, { count: pendingCosignerCount });
}

/**
 * The viewer only has a to-do for each agreement of the update they still sign,
 * so the update's homes are narrowed to those agreements' homes.
 */
function homesStillToSign(
  group: ReplacementGroupMetadata,
  todos: { metadata?: InvitationSentToDoMetadata }[],
): HomeInAgreement[] {
  const openHomeIds = new Set(
    todos
      .filter(({ metadata }) => metadata?.replacementGroup?.id === group.id)
      .flatMap(({ metadata }) => metadata?.homesInAgreement ?? [])
      .map(({ homeUniqueId }) => homeUniqueId),
  );
  const homes = group.homes.filter(({ homeUniqueId }) =>
    openHomeIds.has(homeUniqueId),
  );

  return homes.length > 0 ? homes : group.homes;
}

export function groupInvitationSentToDos<
  T extends { metadata?: InvitationSentToDoMetadata },
>(todos: T[]): GroupedToDo<T>[] {
  const seenGroupIds = new Set<number>();

  return todos.flatMap((todo) => {
    const group = todo.metadata?.replacementGroup;

    if (!group) {
      return [{ todo, homes: todo.metadata?.homesInAgreement ?? [] }];
    }

    if (seenGroupIds.has(group.id)) {
      return [];
    }

    seenGroupIds.add(group.id);

    return [{ todo, homes: homesStillToSign(group, todos), group }];
  });
}

function isInvitationSentToDo<T extends { type: string; metadata?: unknown }>(
  todo: T,
): todo is T & { metadata: InvitationSentToDoMetadata } {
  return (
    todo.type === 'AgreementFlowOpen' &&
    typeof todo.metadata === 'object' &&
    todo.metadata !== null
  );
}

export function groupToDoList<T extends { type: string; metadata?: unknown }>(
  todos: T[],
): GroupedToDo<T>[] {
  const groupedInvitations = new Map<T, GroupedToDo<T>>(
    groupInvitationSentToDos(todos.filter(isInvitationSentToDo)).map(
      (grouped) => [grouped.todo, grouped],
    ),
  );

  return todos.flatMap((todo): GroupedToDo<T>[] => {
    if (todo.type !== 'AgreementFlowOpen') {
      return [{ todo, homes: [] }];
    }

    const grouped = groupedInvitations.get(todo);

    return grouped ? [grouped] : [];
  });
}
